import { Dispatch, SetStateAction, useCallback } from 'react'

import { isResponseError } from '@marketplace-web/core-api/api-client-util'
import { useSession } from '@marketplace-web/shared/session'
import { UiState } from '@marketplace-web/shared/ui-state'
import {
  getPhotos,
  createPhoto,
  transformMediaUploadPhotoDtos,
  CreatePhotoResp,
} from '@marketplace-web/item-upload/media-upload-data'
import {
  EntryType,
  getPhotoTips,
  PhotoTipModel,
  transformPhotoTipDtos,
} from '@marketplace-web/item-upload/photo-tips-data'

import { createPhotoFormData } from '../utils'
import useMediaSelectContext from './useMediaSelectContext'

type Props = {
  photoEntryId: number | string | null
  tempUuid?: string
  type: string
  setUiState: Dispatch<SetStateAction<UiState>>
  photosCount: number
  setPhotoTips: Dispatch<SetStateAction<Array<PhotoTipModel>>>
  onUploadFilesStart: () => void
  onUploadFilesEnd: (uiState: UiState, photosCount: number, userItemCount: number) => void
}

const useMediaSelectUtils = ({
  setUiState,
  tempUuid,
  photoEntryId,
  type,
  photosCount,
  setPhotoTips,
  onUploadFilesStart,
  onUploadFilesEnd,
}: Props) => {
  const { user } = useSession()
  const itemCount = user?.item_count || 0
  const { addPhotos } = useMediaSelectContext()

  const fetchPhotos = useCallback(async () => {
    if (!photoEntryId || !tempUuid) return

    setUiState(UiState.Pending)
    const response = await getPhotos({ type, photoEntryId, tempUuid })

    if (isResponseError(response)) {
      setUiState(UiState.Failure)

      return
    }

    setUiState(UiState.Success)
    const transformedPhotos = transformMediaUploadPhotoDtos(response.photos_dto)
    addPhotos(transformedPhotos)
  }, [addPhotos, photoEntryId, setUiState, tempUuid, type])

  const fetchPhotoTips = useCallback(async () => {
    const response = await getPhotoTips(EntryType.NewItem)

    if (isResponseError(response)) return

    const photoTipsModel = transformPhotoTipDtos(response.photo_tips)
    setPhotoTips(photoTipsModel)
  }, [setPhotoTips])

  const uploadPhotoFiles = useCallback(
    async ({ files, uploadType }: { files: Array<File>; uploadType: string }) => {
      try {
        setUiState(UiState.Pending)
        onUploadFilesStart()

        const convertedPhotos = files.map(file => createPhotoFormData(file, uploadType, tempUuid))
        const responses = await Promise.all(convertedPhotos.map(createPhoto))
        const hasError = responses.some(response => isResponseError(response))

        if (hasError) {
          setUiState(UiState.Failure)
          onUploadFilesEnd(UiState.Failure, photosCount, itemCount)

          return
        }

        const transformedPhotos = transformMediaUploadPhotoDtos(responses as Array<CreatePhotoResp>)

        setUiState(UiState.Success)
        addPhotos(transformedPhotos)
        onUploadFilesEnd(UiState.Success, photosCount, itemCount)
      } catch (error) {
        setUiState(UiState.Failure)
        onUploadFilesEnd(UiState.Failure, photosCount, itemCount)
      }
    },
    [addPhotos, itemCount, photosCount, setUiState, tempUuid, onUploadFilesStart, onUploadFilesEnd],
  )

  return {
    fetchPhotos,
    fetchPhotoTips,
    uploadPhotoFiles,
  }
}

export default useMediaSelectUtils
